import type { AxiosInstance } from "axios";
import {
  EventIdPublic,
  EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import type { AdminRecomputeReasonsToMeetBody } from "@shared/schemas/admin-participant-reasons-to-meet.schemas";

export function createParticipantInspectorAdminApi(apiClient: AxiosInstance) {
  function participantBase(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): string {
    return `/api/v1/admin/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}`;
  }

  // Reads return unknown: the response is validated by the repo hook's schema
  // parse, not here. An axios response generic would be an unchecked cast, not
  // validation.
  async function getAssumptions(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): Promise<unknown> {
    const response = await apiClient.get(
      `${participantBase(eventIdPublic, participantIdPublic)}/assumptions`,
    );
    return response.data;
  }

  async function getEnrichment(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): Promise<unknown> {
    const response = await apiClient.get(
      `${participantBase(eventIdPublic, participantIdPublic)}/enrichment`,
    );
    return response.data;
  }

  // Regenerates the assumption block from the participant's enrichment; the work
  // itself is a job, so this returns as soon as it is scheduled.
  async function regenerateAssumptions(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): Promise<void> {
    await apiClient.put(
      `${participantBase(eventIdPublic, participantIdPublic)}/assumptions/regenerate`,
    );
  }

  async function getReasonsToMeet(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): Promise<unknown> {
    const response = await apiClient.get(
      `${participantBase(eventIdPublic, participantIdPublic)}/reasons-to-meet`,
    );
    return response.data;
  }

  // Queues the computation — `null` for everyone in the event; the work
  // itself is a job.
  async function recomputeReasonsToMeet(
    eventIdPublic: EventIdPublic,
    participantIdsPublic: readonly EventParticipantIdPublic[] | null,
    force: boolean,
  ): Promise<void> {
    const body: AdminRecomputeReasonsToMeetBody = {
      participantIdsPublic:
        participantIdsPublic === null ? null : [...participantIdsPublic],
      force,
    };
    await apiClient.put(
      `/api/v1/admin/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/reasons-to-meet/recompute`,
      body,
    );
  }

  // Queues one recompute per participant in the event; each skips an unchanged
  // card, so a second press is cheap.
  async function regenerateDirectoryEmbeddings(
    eventIdPublic: EventIdPublic,
  ): Promise<void> {
    await apiClient.put(
      `/api/v1/admin/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/directory-embeddings/regenerate`,
    );
  }

  return {
    getAssumptions,
    getEnrichment,
    regenerateAssumptions,
    getReasonsToMeet,
    recomputeReasonsToMeet,
    regenerateDirectoryEmbeddings,
  };
}
